import { useEffect, useRef } from 'react';
import {
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  Checkbox,
} from '@chakra-ui/react';

import { ValidatedField, CurrencyInput } from '..';
import {
  useSumpPumpLogic,
  useBackwaterValveLogic,
  useValidationSchema,
  type SumpPumpLocation,
  type BackwaterValveLocation,
} from '../../hooks';
import { useAnswerCodes } from '../../hooks';
import type { CoverageSectionProps } from './types';

interface ExtendedLocation extends SumpPumpLocation, BackwaterValveLocation {
  alarmed_sump_pump?: string;
  province?: string;
}

interface WaterDamagesSectionProps extends CoverageSectionProps {
  location: ExtendedLocation;
}

const WaterDamagesSection = ({
  coverages,
  updateCoverage,
  location,
}: WaterDamagesSectionProps) => {
  const { hasSumpPump } = useSumpPumpLogic(location);
  const { hasBackwaterValve } = useBackwaterValveLogic(location);
  const { getLabel } = useAnswerCodes();
  const { isFieldVisible } = useValidationSchema();
  const prevHasSumpPump = useRef<boolean | null>(null);

  const showAboveGroundWater = isFieldVisible(
    'location',
    'coverages.above_ground_water_enabled',
    { province: location.province },
  );

  const isAlarmedYes = (() => {
    const alarmedValue = location.alarmed_sump_pump || '';
    if (!alarmedValue) return false;
    const label = getLabel(alarmedValue, 'alarmed_sump_pump');
    return label.toLowerCase() === 'yes';
  })();

  useEffect(() => {
    const hasSumpPumpChanged =
      prevHasSumpPump.current !== null &&
      prevHasSumpPump.current !== hasSumpPump;
    prevHasSumpPump.current = hasSumpPump;

    if (hasSumpPump !== coverages.sump_pump_pit_enabled) {
      updateCoverage('sump_pump_pit_enabled', hasSumpPump);
    }

    if (hasSumpPumpChanged && !hasSumpPump) {
      if (coverages.alarmed_sump_pump_enabled) {
        updateCoverage('alarmed_sump_pump_enabled', false);
      }
    }
  }, [
    hasSumpPump,
    coverages.sump_pump_pit_enabled,
    coverages.alarmed_sump_pump_enabled,
    updateCoverage,
  ]);

  useEffect(() => {
    if (hasSumpPump && isAlarmedYes !== coverages.alarmed_sump_pump_enabled) {
      updateCoverage('alarmed_sump_pump_enabled', isAlarmedYes);
    }
  }, [
    hasSumpPump,
    isAlarmedYes,
    coverages.alarmed_sump_pump_enabled,
    updateCoverage,
  ]);

  useEffect(() => {
    if (hasBackwaterValve !== coverages.backflow_valve_enabled) {
      updateCoverage('backflow_valve_enabled', hasBackwaterValve);
    }
  }, [hasBackwaterValve, coverages.backflow_valve_enabled, updateCoverage]);

  useEffect(() => {
    if (!showAboveGroundWater && coverages.above_ground_water_enabled) {
      updateCoverage('above_ground_water_enabled', false);
      updateCoverage('above_ground_water_limit_cad', null);
      updateCoverage('above_ground_water_deductible_cad', null);
    }
  }, [
    showAboveGroundWater,
    coverages.above_ground_water_enabled,
    updateCoverage,
  ]);

  return (
    <Grid templateColumns="1fr 1fr" gap={4}>
      <GridItem>
        <Text fontWeight="700" mb={3}>
          Water Damages Extensions
        </Text>
        <HStack spacing={6}>
          <Checkbox
            isChecked={coverages.sump_pump_pit_enabled}
            colorScheme="blue"
            isDisabled
            title="Derived from Sump Pump Type in property details"
          >
            <Text
              fontSize="sm"
              color={coverages.sump_pump_pit_enabled ? 'inherit' : 'gray.400'}
            >
              Sump Pump Pit
            </Text>
          </Checkbox>
          <Checkbox
            isChecked={coverages.alarmed_sump_pump_enabled}
            colorScheme="blue"
            isDisabled
            title="Derived from Alarmed Sump Pump in property details"
          >
            <Text
              fontSize="sm"
              color={
                coverages.alarmed_sump_pump_enabled ? 'inherit' : 'gray.400'
              }
            >
              Alarmed Sump Pump
            </Text>
          </Checkbox>
        </HStack>
        <HStack spacing={3} align="flex-end" mt={4}>
          <Checkbox
            isChecked={coverages.sewer_backup_enabled}
            onChange={(e) =>
              updateCoverage('sewer_backup_enabled', e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm">Sewer Backup</Text>
          </Checkbox>
          {coverages.sewer_backup_enabled && (
            <>
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.sewer_backup_limit_cad"
                label="Limit"
                labelSize="xs"
                formControlProps={{ w: '90px' }}
              >
                <CurrencyInput
                  value={coverages.sewer_backup_limit_cad ?? ''}
                  onChange={(_, value) =>
                    updateCoverage('sewer_backup_limit_cad', value)
                  }
                  placeholder="BASE"
                />
              </ValidatedField>
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.sewer_backup_deductible_cad"
                label="Deductible"
                labelSize="xs"
                formControlProps={{ w: '90px' }}
              >
                <CurrencyInput
                  value={coverages.sewer_backup_deductible_cad ?? ''}
                  onChange={(_, value) =>
                    updateCoverage('sewer_backup_deductible_cad', value)
                  }
                  placeholder="BASE"
                />
              </ValidatedField>
            </>
          )}
        </HStack>
      </GridItem>
      <GridItem>
        <VStack align="stretch" spacing={2} mt={8}>
          <HStack spacing={3} align="flex-end">
            <Checkbox
              isChecked={coverages.ground_water_enabled}
              onChange={(e) =>
                updateCoverage('ground_water_enabled', e.target.checked)
              }
              colorScheme="blue"
            >
              <Text fontSize="sm">Ground Water</Text>
            </Checkbox>
            {coverages.ground_water_enabled && (
              <>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.ground_water_limit_cad"
                  label="Limit"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.ground_water_limit_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('ground_water_limit_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.ground_water_deductible_cad"
                  label="Deductible"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.ground_water_deductible_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('ground_water_deductible_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
              </>
            )}
          </HStack>
          {showAboveGroundWater && (
            <HStack spacing={3} align="flex-end">
              <Checkbox
                isChecked={coverages.above_ground_water_enabled}
                onChange={(e) =>
                  updateCoverage('above_ground_water_enabled', e.target.checked)
                }
                colorScheme="blue"
              >
                <Text fontSize="sm">Above Ground Water</Text>
              </Checkbox>
              {coverages.above_ground_water_enabled && (
                <>
                  <ValidatedField
                    itemPath="location"
                    fieldPath="coverages.above_ground_water_limit_cad"
                    label="Limit"
                    labelSize="xs"
                    formControlProps={{ w: '80px' }}
                  >
                    <CurrencyInput
                      value={coverages.above_ground_water_limit_cad ?? ''}
                      onChange={(_, value) =>
                        updateCoverage('above_ground_water_limit_cad', value)
                      }
                      placeholder="BASE"
                    />
                  </ValidatedField>
                  <ValidatedField
                    itemPath="location"
                    fieldPath="coverages.above_ground_water_deductible_cad"
                    label="Deductible"
                    labelSize="xs"
                    formControlProps={{ w: '80px' }}
                  >
                    <CurrencyInput
                      value={coverages.above_ground_water_deductible_cad ?? ''}
                      onChange={(_, value) =>
                        updateCoverage(
                          'above_ground_water_deductible_cad',
                          value,
                        )
                      }
                      placeholder="BASE"
                    />
                  </ValidatedField>
                </>
              )}
            </HStack>
          )}
          <HStack spacing={3} align="flex-end">
            <Checkbox
              isChecked={coverages.overland_water_enabled}
              onChange={(e) =>
                updateCoverage('overland_water_enabled', e.target.checked)
              }
              colorScheme="blue"
            >
              <Text fontSize="sm">Overland Water</Text>
            </Checkbox>
            {coverages.overland_water_enabled && (
              <>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.overland_water_limit_cad"
                  label="Limit"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.overland_water_limit_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('overland_water_limit_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.overland_water_deductible_cad"
                  label="Deductible"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.overland_water_deductible_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('overland_water_deductible_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
              </>
            )}
          </HStack>
          <HStack spacing={3} align="flex-end">
            <Checkbox
              isChecked={coverages.service_line_enabled}
              onChange={(e) =>
                updateCoverage('service_line_enabled', e.target.checked)
              }
              colorScheme="blue"
            >
              <Text fontSize="sm">Service Line</Text>
            </Checkbox>
            {coverages.service_line_enabled && (
              <>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.service_line_limit_cad"
                  label="Limit"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.service_line_limit_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('service_line_limit_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.service_line_deductible_cad"
                  label="Deductible"
                  labelSize="xs"
                  formControlProps={{ w: '80px' }}
                >
                  <CurrencyInput
                    value={coverages.service_line_deductible_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('service_line_deductible_cad', value)
                    }
                    placeholder="BASE"
                  />
                </ValidatedField>
              </>
            )}
          </HStack>
          <Checkbox
            isChecked={coverages.backflow_valve_enabled}
            colorScheme="blue"
            isDisabled
            title="Derived from Backwater Valve Type in property details"
          >
            <Text
              fontSize="sm"
              color={coverages.backflow_valve_enabled ? 'inherit' : 'gray.400'}
            >
              Backup/Backflow Valve
            </Text>
          </Checkbox>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default WaterDamagesSection;
